export const NEW_YEAR_PREVIEW_STORAGE_KEY = 'developer_new_year_interface_preview';
export const NEW_YEAR_DISABLED_STORAGE_KEY = 'user_new_year_interface_disabled';
export const NEW_YEAR_TIME_ZONE = 'Europe/Moscow';
export const NEW_YEAR_PREVIEW_CHANGED_EVENT = 'newYearPreviewChanged';
export const JINGLE_BELLS_STATE_CHANGED_EVENT = 'jingleBellsStateChanged';

const getMoscowMonthAndDay = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: NEW_YEAR_TIME_ZONE,
    month: 'numeric',
    day: 'numeric'
  }).formatToParts(date);

  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);

  if (!month || !day) return null;
  return { month, day };
};

export const isNewYearSeason = (date = new Date()) => {
  const moscowDate = getMoscowMonthAndDay(date);
  if (!moscowDate) return false;

  return (moscowDate.month === 12 && moscowDate.day >= 21)
    || (moscowDate.month === 1 && moscowDate.day <= 10);
};

export const isNewYearInterfaceEnabled = ({
  date = new Date(),
  userType = ''
} = {}) => {
  if (typeof localStorage === 'undefined') return false;
  const inSeason = isNewYearSeason(date);
  const isDev = String(userType).trim().toLowerCase() === 'developer';

  if (inSeason) {
    // В праздничный сезон тема включена по умолчанию, если пользователь явно не отключил
    return localStorage.getItem(NEW_YEAR_DISABLED_STORAGE_KEY) !== 'true';
  }

  // Вне сезона доступно только разработчику для предпросмотра
  return isDev && localStorage.getItem(NEW_YEAR_PREVIEW_STORAGE_KEY) === 'true';
};

export const writeNewYearInterfaceEnabled = (enabled, {
  date = new Date(),
  userType = ''
} = {}) => {
  if (typeof localStorage === 'undefined') return;
  const nextValue = Boolean(enabled);
  const inSeason = isNewYearSeason(date);

  if (inSeason) {
    if (!nextValue) {
      localStorage.setItem(NEW_YEAR_DISABLED_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(NEW_YEAR_DISABLED_STORAGE_KEY);
    }
  } else {
    localStorage.setItem(NEW_YEAR_PREVIEW_STORAGE_KEY, String(nextValue));
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(NEW_YEAR_PREVIEW_CHANGED_EVENT, { detail: nextValue }));
  }
};

export const readNewYearPreviewEnabled = () => {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(NEW_YEAR_PREVIEW_STORAGE_KEY) === 'true';
};

export const writeNewYearPreviewEnabled = (enabled) => {
  writeNewYearInterfaceEnabled(enabled, { userType: 'developer' });
};

export const shouldShowNewYearInterface = ({
  date = new Date(),
  userType = '',
  previewEnabled = false,
  seasonDisabled = false
} = {}) => {
  const inSeason = isNewYearSeason(date);
  const isDev = String(userType).trim().toLowerCase() === 'developer';

  if (inSeason) {
    return !seasonDisabled;
  }

  return isDev && Boolean(previewEnabled);
};

// ============================================================
// Праздничная мелодия «Jingle Bells» (Джингл Белс)
// ============================================================

export const JINGLE_BELLS_MELODY = [
  // Jingle bells,
  { note: 'E5', freq: 659.25, dur: 0.36, text: 'Jin-' },
  { note: 'E5', freq: 659.25, dur: 0.36, text: 'gle' },
  { note: 'E5', freq: 659.25, dur: 0.72, text: 'bells,' },
  { note: 'rest', freq: 0, dur: 0.16 },
  // jingle bells,
  { note: 'E5', freq: 659.25, dur: 0.36, text: 'Jin-' },
  { note: 'E5', freq: 659.25, dur: 0.36, text: 'gle' },
  { note: 'E5', freq: 659.25, dur: 0.72, text: 'bells,' },
  { note: 'rest', freq: 0, dur: 0.16 },
  // jingle all the way!
  { note: 'E5', freq: 659.25, dur: 0.36, text: 'jin-' },
  { note: 'G5', freq: 783.99, dur: 0.36, text: 'gle' },
  { note: 'C5', freq: 523.25, dur: 0.52, text: 'all' },
  { note: 'D5', freq: 587.33, dur: 0.20, text: 'the' },
  { note: 'E5', freq: 659.25, dur: 0.90, text: 'way!' },
  { note: 'rest', freq: 0, dur: 0.25 },
  // Oh what fun it is to ride
  { note: 'F5', freq: 698.46, dur: 0.36, text: 'Oh' },
  { note: 'F5', freq: 698.46, dur: 0.36, text: 'what' },
  { note: 'F5', freq: 698.46, dur: 0.50, text: 'fun' },
  { note: 'F5', freq: 698.46, dur: 0.22, text: 'it' },
  { note: 'F5', freq: 698.46, dur: 0.36, text: 'is' },
  { note: 'E5', freq: 659.25, dur: 0.36, text: 'to' },
  { note: 'E5', freq: 659.25, dur: 0.36, text: 'ride' },
  { note: 'E5', freq: 659.25, dur: 0.18, text: 'in' },
  { note: 'E5', freq: 659.25, dur: 0.18, text: 'a' },
  // in a one-horse open sleigh, hey!
  { note: 'E5', freq: 659.25, dur: 0.36, text: 'one' },
  { note: 'D5', freq: 587.33, dur: 0.36, text: 'horse' },
  { note: 'D5', freq: 587.33, dur: 0.36, text: 'o-' },
  { note: 'E5', freq: 659.25, dur: 0.36, text: 'pen' },
  { note: 'D5', freq: 587.33, dur: 0.72, text: 'sleigh,' },
  { note: 'G5', freq: 783.99, dur: 0.90, text: 'hey!' }
];

export const JINGLE_BELLS_TAP_NOTES = JINGLE_BELLS_MELODY.filter((item) => item.freq > 0);

let audioContextInstance = null;
let jingleBellsPlaying = false;
let activeMelodyTimeouts = [];
let interactiveTapStep = 0;
let interactiveTapResetTimer = null;

const getAudioContext = () => {
  if (typeof window === 'undefined') return null;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;

  if (!audioContextInstance) {
    audioContextInstance = new AudioCtx();
  }
  if (audioContextInstance.state === 'suspended') {
    audioContextInstance.resume().catch(() => {});
  }
  return audioContextInstance;
};

export const playSingleBellNote = (freq, duration = 0.35, volume = 0.16) => {
  if (!freq || typeof window === 'undefined') return;
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;

    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(freq, now);

    const osc2 = ctx.createOscillator();
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(freq * 2.76, now);

    const osc3 = ctx.createOscillator();
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(freq * 5.4, now);

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.0001, now);
    masterGain.gain.linearRampToValueAtTime(volume, now + 0.005);

    // Четкое затухание для колокольчика: исключает фазовое наложение при повторе одинаковых нот (E5, E5, E5)
    const decayDuration = duration >= 0.6 ? duration * 0.95 : Math.min(duration * 0.88, 0.32);
    masterGain.gain.exponentialRampToValueAtTime(0.0001, now + decayDuration);

    const gain2 = ctx.createGain();
    gain2.gain.setValueAtTime(0.24, now);
    osc2.connect(gain2);
    gain2.connect(masterGain);

    const gain3 = ctx.createGain();
    gain3.gain.setValueAtTime(0.12, now);
    osc3.connect(gain3);
    gain3.connect(masterGain);

    osc1.connect(masterGain);
    masterGain.connect(ctx.destination);

    const stopTime = now + decayDuration + 0.03;
    osc1.start(now);
    osc2.start(now);
    osc3.start(now);
    osc1.stop(stopTime);
    osc2.stop(stopTime);
    osc3.stop(stopTime);
  } catch (err) {
    // Безопасно игнорируем политики браузера
  }
};

export const playJingleBellsStep = () => {
  const currentNote = JINGLE_BELLS_TAP_NOTES[interactiveTapStep];
  if (currentNote) {
    playSingleBellNote(currentNote.freq, currentNote.dur, 0.16);
  }

  interactiveTapStep = (interactiveTapStep + 1) % JINGLE_BELLS_TAP_NOTES.length;

  if (typeof window !== 'undefined') {
    if (interactiveTapResetTimer) window.clearTimeout(interactiveTapResetTimer);
    interactiveTapResetTimer = window.setTimeout(() => {
      interactiveTapStep = 0;
      interactiveTapResetTimer = null;
    }, 3500);
  }
};

export const playJingleBellsTune = (onFinish = null) => {
  stopJingleBellsTune();

  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  jingleBellsPlaying = true;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(JINGLE_BELLS_STATE_CHANGED_EVENT, { detail: true }));
  }

  // Небольшой буфер 45мс перед первой нотой дает звуковому чипу мобильного браузера
  // выйти из suspended-состояния, исключая клиппинг или «съедание» первой ноты
  let accumulatedDelayMs = 45;

  JINGLE_BELLS_MELODY.forEach((item) => {
    const delay = accumulatedDelayMs;
    const durMs = Math.round(item.dur * 1000);
    accumulatedDelayMs += durMs;

    if (item.freq > 0) {
      const timer = window.setTimeout(() => {
        if (!jingleBellsPlaying) return;
        playSingleBellNote(item.freq, item.dur, 0.18);
      }, delay);
      activeMelodyTimeouts.push(timer);
    }
  });

  const finishTimer = window.setTimeout(() => {
    stopJingleBellsTune();
    if (typeof onFinish === 'function') onFinish();
  }, accumulatedDelayMs + 300);
  activeMelodyTimeouts.push(finishTimer);
};

export const stopJingleBellsTune = () => {
  if (activeMelodyTimeouts.length > 0) {
    activeMelodyTimeouts.forEach((id) => window.clearTimeout(id));
    activeMelodyTimeouts = [];
  }
  jingleBellsPlaying = false;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(JINGLE_BELLS_STATE_CHANGED_EVENT, { detail: false }));
  }
};

export const isJingleBellsPlaying = () => jingleBellsPlaying;

export const playHolidayChime = () => {
  playJingleBellsStep();
};

export const triggerHolidayBurst = (originX, originY) => {
  if (typeof document === 'undefined') return;

  const container = document.createElement('div');
  container.className = 'new-year-particle-container';
  container.style.left = `${originX}px`;
  container.style.top = `${originY}px`;
  document.body.appendChild(container);

  // Аккуратные частицы умеренного размера (не гигантские и не микроскопические)
  const PARTICLE_COUNT = 10;
  const ICONS = ['✨', '⭐', '❄', '✦', '✧'];
  const COLORS = ['#f59e0b', '#38bdf8', '#ef4444', '#10b981', '#c084fc'];

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const particle = document.createElement('span');
    particle.className = 'new-year-burst-particle';
    const angle = (Math.PI * 2 * i) / PARTICLE_COUNT + (Math.random() * 0.35 - 0.17);
    // Расстояние разлёта 40-80px: комфортно вылетает из-под пальца
    const distance = 40 + Math.random() * 42;
    const dx = Math.cos(angle) * distance;
    const dy = Math.sin(angle) * distance;
    const icon = ICONS[Math.floor(Math.random() * ICONS.length)];
    const color = COLORS[Math.floor(Math.random() * COLORS.length)];

    particle.textContent = icon;
    particle.style.color = color;
    particle.style.setProperty('--dx', `${dx.toFixed(1)}px`);
    particle.style.setProperty('--dy', `${dy.toFixed(1)}px`);
    particle.style.setProperty('--rotate', `${(Math.random() * 360).toFixed(0)}deg`);
    particle.style.setProperty('--burst-duration', `${0.65 + Math.random() * 0.18}s`);

    container.appendChild(particle);
  }

  window.setTimeout(() => {
    container.remove();
  }, 950);
};

export const triggerHolidayFireworks = () => {
  if (typeof window === 'undefined') return;

  const width = window.innerWidth;
  const height = window.innerHeight;

  const burstLocations = [
    { x: width * 0.25, y: height * 0.35, delay: 0 },
    { x: width * 0.75, y: height * 0.30, delay: 180 },
    { x: width * 0.50, y: height * 0.25, delay: 360 },
    { x: width * 0.35, y: height * 0.45, delay: 540 }
  ];

  burstLocations.forEach(({ x, y, delay }) => {
    window.setTimeout(() => {
      triggerHolidayBurst(x, y);
      playJingleBellsStep();
    }, delay);
  });
};
